import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  formatRelatedEntityCountLabel,
} from '@/features/metrics/components/statusMetricCards';
import { queryPipelineRunSummary, queryPipelineSummary } from '@/features/pipelines/data';
import { buildPreviousCustomWindow } from '@/features/pipelines/utils/buildMetricCharts';
import {
  buildPipelineCountsFromRunSummary,
  pipelineStatusDisplayOptions,
  type PipelineStatusKey,
} from '@/features/quality/components/qualityStatusDataHelpers';
import { formatRangeWindowNoDataText, type QualityRange } from '@/features/quality/hooks';
import { buildPipelineMetricOptions } from '@/features/quality/sections/qualityMetricOptions';
import { buildMetricsChartModelFromBuckets } from '@/features/shared/components/chartPanels/chartPanelAdapters';
import {
  buildMetricSummaryFromBuckets,
  type DailyExecutionMetricBucket,
} from '@/features/shared/utils/chartBucketSeries';
import { buildChartDatesForDayRange } from '@/features/shared/utils/chartDateWindow';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { queryKeys } from '@/shared/hooks';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import { formatStatusLabel } from '@/ui';
import {
  buildContainsFilter,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildBuildPipelineFilters, buildPipelineEntityFilters } from './pipelinePageFilters';
import { shouldLoadPipelineEntitySummaryQuery } from './pipelinePageQueryGates';
import { toPipelineRunDisplayStatusFilters } from './pipelineStatusScope';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { MetricsStatusChartPanelDisplayModel } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { UiTone } from '@/theme/uiTokens';
import type { MetricThresholdGroup } from '@flowqa/shared';
import type { CiDisplayStatusKey } from '@flowqa/shared';

const pipelineDisplayStatuses: CiDisplayStatusKey[] = [
  'success',
  'warning',
  'failed',
  'running',
  'queued',
  'canceled',
  'skipped',
  'neutral',
  'action_required',
  'timed_out',
  'unknown',
];

const optionalPipelineMetricStatuses: PipelineStatusKey[] = [
  'running',
  'queued',
  'warning',
  'canceled',
  'skipped',
  'neutral',
  'action_required',
  'timed_out',
  'unknown',
];

const alwaysVisiblePipelineFilterStatuses: PipelineStatusKey[] = ['success', 'failed'];

const toLatestRunStatusFilterValues = (
  statuses: PipelineStatusKey[],
): Array<'queued' | 'running' | 'completed' | 'failed' | null> => {
  const mapped = statuses.flatMap(
    (status): Array<'queued' | 'running' | 'completed' | 'failed' | null> => {
      if (status === 'none') return [null];
      if (status === 'success') return ['completed'];
      if (status === 'running') return ['running'];
      if (status === 'queued') return ['queued'];
      return ['failed'];
    },
  );
  return Array.from(new Set(mapped));
};

type UsePipelinesOverviewModelArgs = {
  isAuthenticated: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  range: QualityRange;
  rangeStartIso: string | null;
  rangeEndIso: string | null;
  selectedStartDay: string | null;
  selectedEndDay: string | null;
  previousRangeStartIso: string | null;
  previousRangeEndIso: string | null;
  debouncedSearchValue: string;
  selectedStatuses: PipelineStatusKey[];
  selectedTags: string[];
  hasUnsupportedChartStatusSelection: boolean;
  thresholds: MetricThresholdGroup;
  showDelta: boolean;
};

type UsePipelinesOverviewModelResult = {
  statusFilterOptions: Array<{ value: PipelineStatusKey; label: string }>;
  pipelinesNoRowsText: string;
  pipelineMetricCards: MetricCardItem[];
  modelOverride: MetricsStatusChartPanelDisplayModel;
  currentSummaryLoading: boolean;
  hasCurrentSummary: boolean;
};

export const usePipelinesOverviewModel = ({
  isAuthenticated,
  selectedProjectId,
  selectedOrganisationId,
  selectedPipelineIds,
  range,
  rangeStartIso,
  rangeEndIso,
  selectedStartDay,
  selectedEndDay,
  previousRangeStartIso,
  previousRangeEndIso,
  debouncedSearchValue,
  selectedStatuses,
  selectedTags,
  hasUnsupportedChartStatusSelection,
  thresholds,
  showDelta,
}: UsePipelinesOverviewModelArgs): UsePipelinesOverviewModelResult => {
  const selectedDisplayStatuses = useMemo<CiDisplayStatusKey[]>(
    () => toPipelineRunDisplayStatusFilters(selectedStatuses),
    [selectedStatuses],
  );
  const hasOnlyNoBuildsSelected =
    selectedStatuses.length > 0 && selectedStatuses.every((status) => status === 'none');
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const pipelineMetricOptions = useMemo(buildPipelineMetricOptions, []);
  const pipelinesNoRowsText = formatRangeWindowNoDataText(
    getEmptyStateMessage('entity.noneFound', { entity: 'pipelineRun' }),
    range,
    hasCustomDates,
  );
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const runSummaryFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('pipelineName', debouncedSearchValue),
        buildBuildPipelineFilters(selectedPipelineIds),
        buildInFilter('displayStatus', selectedDisplayStatuses),
        tagFilters,
      ),
    [debouncedSearchValue, selectedDisplayStatuses, selectedPipelineIds, tagFilters],
  );
  const pipelineSummaryFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('name', debouncedSearchValue),
        buildPipelineEntityFilters(selectedPipelineIds),
        selectedStatuses.length > 0
          ? buildInFilterWithNull(
              'latestRunStatus',
              toLatestRunStatusFilterValues(selectedStatuses),
            )
          : [],
        tagFilters,
      ),
    [debouncedSearchValue, selectedStatuses, selectedPipelineIds, tagFilters],
  );
  const currentPipelineSummaryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
        filters: pipelineSummaryFilters,
      }),
    [pipelineSummaryFilters, rangeEndIso, rangeStartIso, selectedEndDay, selectedStartDay],
  );
  const currentRunSummaryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        latestOnly: isLatestMode,
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
        filters: runSummaryFilters,
      }),
    [runSummaryFilters, isLatestMode, rangeEndIso, rangeStartIso, selectedEndDay, selectedStartDay],
  );
  const currentPipelineRunSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineRunSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      currentRunSummaryFiltersKey,
    ),
    enabled: isAuthenticated && !hasOnlyNoBuildsSelected,
    queryFn: async () =>
      queryPipelineRunSummary(selectedProjectId, selectedOrganisationId ?? undefined, {
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: runSummaryFilters,
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
        latestOnly: isLatestMode,
      }),
  });
  const currentPipelineSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      currentPipelineSummaryFiltersKey,
    ),
    enabled: shouldLoadPipelineEntitySummaryQuery({
      hasOnlyNoBuildsSelected,
      isAuthenticated,
      runSummarySettled:
        currentPipelineRunSummaryQuery.isFetched || currentPipelineRunSummaryQuery.isError,
    }),
    queryFn: async () =>
      queryPipelineSummary(selectedProjectId, selectedOrganisationId ?? undefined, {
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: pipelineSummaryFilters,
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
      }),
  });
  const currentPipelineRunSummary = currentPipelineRunSummaryQuery.data ?? null;
  const currentPipelineSummary = currentPipelineSummaryQuery.data ?? null;

  const previousChartWindow = useMemo(() => {
    if (isLatestMode || isAllMode) {
      return { startIso: null, endIso: null };
    }
    if (hasCustomDates) {
      return buildPreviousCustomWindow(selectedStartDay, selectedEndDay);
    }
    return { startIso: previousRangeStartIso, endIso: previousRangeEndIso };
  }, [
    hasCustomDates,
    isAllMode,
    isLatestMode,
    previousRangeEndIso,
    previousRangeStartIso,
    selectedEndDay,
    selectedStartDay,
  ]);
  const currentChartDates = useMemo(() => {
    const startDay = selectedStartDay ?? selectedEndDay;
    const endDay = selectedEndDay ?? selectedStartDay;
    const explicitDates = buildChartDatesForDayRange(startDay, endDay);
    if (explicitDates.length > 0) {
      return explicitDates;
    }
    return buildChartDatesForDayRange(
      rangeStartIso?.slice(0, 10) ?? null,
      rangeEndIso?.slice(0, 10) ?? null,
    );
  }, [rangeEndIso, rangeStartIso, selectedEndDay, selectedStartDay]);
  const previousChartDates = useMemo(
    () =>
      buildChartDatesForDayRange(
        previousChartWindow.startIso?.slice(0, 10) ?? null,
        previousChartWindow.endIso?.slice(0, 10) ?? null,
      ),
    [previousChartWindow.endIso, previousChartWindow.startIso],
  );
  const previousRunSummaryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        latestOnly: false,
        metricsStartDay: previousChartWindow.startIso?.slice(0, 10) ?? null,
        metricsEndDay: previousChartWindow.endIso?.slice(0, 10) ?? null,
        filters: runSummaryFilters,
      }),
    [previousChartWindow.endIso, previousChartWindow.startIso, runSummaryFilters],
  );
  const previousPipelineRunSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineRunSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousRunSummaryFiltersKey,
    ),
    enabled: isAuthenticated && showDelta && !hasOnlyNoBuildsSelected,
    queryFn: async () =>
      queryPipelineRunSummary(selectedProjectId, selectedOrganisationId ?? undefined, {
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: runSummaryFilters,
        metricsStartDay: previousChartWindow.startIso?.slice(0, 10) ?? undefined,
        metricsEndDay: previousChartWindow.endIso?.slice(0, 10) ?? undefined,
        latestOnly: false,
      }),
  });
  const previousPipelineRunSummary = previousPipelineRunSummaryQuery.data ?? null;
  const pipelineCounts = useMemo(
    () => buildPipelineCountsFromRunSummary(currentPipelineRunSummary),
    [currentPipelineRunSummary],
  );
  const previousPipelineCounts = useMemo(
    () => buildPipelineCountsFromRunSummary(previousPipelineRunSummary),
    [previousPipelineRunSummary],
  );
  const pipelineTotal = useMemo(
    () => currentPipelineRunSummary?.buildTotal ?? 0,
    [currentPipelineRunSummary?.buildTotal],
  );
  const previousPipelineTotal = useMemo(
    () => previousPipelineRunSummary?.buildTotal ?? 0,
    [previousPipelineRunSummary?.buildTotal],
  );
  const pipelineDeltas = useMemo(
    () => (showDelta ? buildStatusDeltaMap(pipelineCounts, previousPipelineCounts) : undefined),
    [pipelineCounts, previousPipelineCounts, showDelta],
  );
  const relatedPipelineCount = useMemo<number | null>(() => {
    const total = currentPipelineSummary?.total;
    return typeof total === 'number' ? total : null;
  }, [currentPipelineSummary]);
  const currentMetricBucketsByDate = useMemo<ReadonlyMap<string, DailyExecutionMetricBucket>>(
    () =>
      new Map(
        (currentPipelineRunSummary?.buckets ?? []).map((bucket) => {
          const metricBucket: DailyExecutionMetricBucket = {
            total: bucket.total,
            completed: bucket.displayStatusCounts.success + bucket.displayStatusCounts.warning,
            failed:
              bucket.displayStatusCounts.failed +
              bucket.displayStatusCounts.canceled +
              bucket.displayStatusCounts.timed_out,
            durationSumSeconds:
              bucket.averageDurationMs != null ? bucket.averageDurationMs / 1000 : 0,
            durationCount: bucket.averageDurationMs != null ? 1 : 0,
            flakeOutcomes: [],
            flakeScore: bucket.flakeScore,
          };
          return [bucket.day.toISOString().slice(0, 10), metricBucket] as const;
        }),
      ),
    [currentPipelineRunSummary],
  );
  const previousMetricBucketsByDate = useMemo<ReadonlyMap<string, DailyExecutionMetricBucket>>(
    () =>
      new Map(
        (previousPipelineRunSummary?.buckets ?? []).map((bucket) => {
          const metricBucket: DailyExecutionMetricBucket = {
            total: bucket.total,
            completed: bucket.displayStatusCounts.success + bucket.displayStatusCounts.warning,
            failed:
              bucket.displayStatusCounts.failed +
              bucket.displayStatusCounts.canceled +
              bucket.displayStatusCounts.timed_out,
            durationSumSeconds:
              bucket.averageDurationMs != null ? bucket.averageDurationMs / 1000 : 0,
            durationCount: bucket.averageDurationMs != null ? 1 : 0,
            flakeOutcomes: [],
            flakeScore: bucket.flakeScore,
          };
          return [bucket.day.toISOString().slice(0, 10), metricBucket] as const;
        }),
      ),
    [previousPipelineRunSummary],
  );
  const currentMetricSummary = useMemo(
    () => buildMetricSummaryFromBuckets(currentMetricBucketsByDate, currentChartDates),
    [currentChartDates, currentMetricBucketsByDate],
  );
  const previousMetricSummary = useMemo(
    () =>
      showDelta
        ? buildMetricSummaryFromBuckets(previousMetricBucketsByDate, previousChartDates)
        : null,
    [previousChartDates, previousMetricBucketsByDate, showDelta],
  );
  const summaryPassRate = currentMetricSummary.passRate;
  const previousSummaryPassRate = showDelta ? (previousMetricSummary?.passRate ?? null) : null;
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: null,
        previousMetrics: null,
        frequencyOverride: currentMetricSummary.frequencyPerDay,
        previousFrequencyOverride: previousMetricSummary?.frequencyPerDay ?? null,
        avgDurationOverride: currentMetricSummary.avgDurationSeconds,
        previousAvgDurationOverride: previousMetricSummary?.avgDurationSeconds ?? null,
        passRateOverride: summaryPassRate,
        previousPassRateOverride: previousSummaryPassRate,
        flakeScoreOverride:
          currentPipelineRunSummary?.averageExecutionFlakeScore ?? currentMetricSummary.flakeScore,
        previousFlakeScoreOverride:
          previousPipelineRunSummary?.averageExecutionFlakeScore ??
          previousMetricSummary?.flakeScore ??
          null,
        thresholds,
        showDelta,
        visibleMetricKeys: resolveVisibleMetricThresholdKeys(thresholds),
      }),
    [
      currentMetricSummary.avgDurationSeconds,
      currentMetricSummary.frequencyPerDay,
      currentMetricSummary.flakeScore,
      currentPipelineRunSummary?.averageExecutionFlakeScore,
      previousMetricSummary?.avgDurationSeconds,
      previousMetricSummary?.frequencyPerDay,
      previousMetricSummary?.flakeScore,
      previousPipelineRunSummary?.averageExecutionFlakeScore,
      previousSummaryPassRate,
      showDelta,
      summaryPassRate,
      thresholds,
    ],
  );
  const currentStatusCountsByDate = useMemo<
    ReadonlyMap<string, Record<CiDisplayStatusKey, number>>
  >(
    () =>
      new Map(
        (currentPipelineRunSummary?.buckets ?? []).map(
          (bucket) => [bucket.day.toISOString().slice(0, 10), bucket.displayStatusCounts] as const,
        ),
      ),
    [currentPipelineRunSummary],
  );
  const visibleBuildStatuses = useMemo(
    () =>
      selectedStatuses.length > 0
        ? pipelineDisplayStatuses.filter((status) => selectedStatuses.includes(status))
        : pipelineDisplayStatuses,
    [pipelineDisplayStatuses, selectedStatuses],
  );
  const statusSeries = useMemo(
    () =>
      visibleBuildStatuses.map((status) => ({
        key: status,
        label: formatStatusLabel(status),
        colorKey: (status === 'success'
          ? 'green'
          : status === 'warning' || status === 'skipped' || status === 'action_required'
            ? 'orange'
            : status === 'failed' || status === 'canceled' || status === 'timed_out'
              ? 'red'
              : status === 'running'
                ? 'blue'
                : 'neutral') as UiTone,
      })),
    [visibleBuildStatuses],
  );
  const pipelineMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: pipelineMetricOptions,
        counts: pipelineCounts,
        deltas: pipelineDeltas,
        total: pipelineTotal,
        totalSecondaryValue: isLatestMode
          ? undefined
          : formatRelatedEntityCountLabel(relatedPipelineCount, 'Pipeline'),
        previousCounts: showDelta ? previousPipelineCounts : undefined,
        previousTotal: showDelta ? previousPipelineTotal : undefined,
        loading:
          currentPipelineRunSummaryQuery.isFetching && !currentPipelineRunSummaryQuery.isError,
        showDelta,
        totalLabel: 'Pipeline Runs',
        hideZeroCountStatuses: optionalPipelineMetricStatuses,
      }).filter((card) => card.id !== 'none'),
    [
      currentPipelineRunSummaryQuery.isError,
      currentPipelineRunSummaryQuery.isFetching,
      pipelineCounts,
      pipelineDeltas,
      pipelineMetricOptions,
      pipelineTotal,
      previousPipelineCounts,
      previousPipelineTotal,
      relatedPipelineCount,
      showDelta,
    ],
  );
  const statusFilterOptions = useMemo(
    () =>
      pipelineStatusDisplayOptions.filter((option) => {
        if (alwaysVisiblePipelineFilterStatuses.includes(option.value)) {
          return true;
        }
        if (selectedStatuses.includes(option.value)) {
          return true;
        }
        return (pipelineCounts[option.value] ?? 0) > 0;
      }),
    [pipelineCounts, selectedStatuses],
  );
  const modelOverride = useMemo(
    () =>
      buildMetricsChartModelFromBuckets({
        statusCountsByDate: hasOnlyNoBuildsSelected ? new Map() : currentStatusCountsByDate,
        visibleStatuses: hasOnlyNoBuildsSelected ? [] : visibleBuildStatuses,
        statusSeries: hasOnlyNoBuildsSelected ? [] : statusSeries,
        metricBucketsByDate: currentMetricBucketsByDate,
        chartDates: currentChartDates,
        metricValueItems,
        showMetricCharts: !hasUnsupportedChartStatusSelection,
      }),
    [
      currentChartDates,
      currentMetricBucketsByDate,
      currentStatusCountsByDate,
      hasOnlyNoBuildsSelected,
      hasUnsupportedChartStatusSelection,
      metricValueItems,
      statusSeries,
      visibleBuildStatuses,
    ],
  );

  return {
    statusFilterOptions,
    pipelinesNoRowsText,
    pipelineMetricCards,
    modelOverride,
    currentSummaryLoading:
      currentPipelineRunSummaryQuery.isFetching && !currentPipelineRunSummaryQuery.isError,
    hasCurrentSummary: currentPipelineRunSummary !== null,
  };
};
